import { buildWordTool, type WordTool } from './builder';
import { VALID_STYLES, preview } from './constants';

export const writingTools: WordTool[] = [
  buildWordTool({
    name: 'insert_content',
    description:
      'Insert one or more styled paragraphs. ONE CALL, MANY OPERATIONS: use `blocks` for multiple paragraphs, and optionally set paragraph-level formatting (alignment, spacing, indent) on the inserted content so you never need a follow-up format_paragraph call. Target by `location` (document- or selection-relative) OR by `target_query` (find text and replace it in one call — replaces select_text+insert_content). NEVER fake a heading with format_text — set `style` here.',
    parameters: {
      type: 'object',
      properties: {
        text: {
          type: 'string',
          description:
            'Single paragraph to insert. Mutually exclusive with blocks. Example: "The quarterly results exceeded expectations."',
        },
        blocks: {
          type: 'array',
          description:
            'Insert multiple paragraphs in one call. Array of {text, style?} objects. Mutually exclusive with text/style. Example: [{"text":"Summary","style":"Heading1"},{"text":"Revenue grew 12%."}]. Always prefer this over multiple insert_content calls.',
        },
        style: {
          type: 'string',
          description:
            'Built-in Word style for single-paragraph insertion (ignored when using blocks). Valid: Normal, Heading1, Heading2, Heading3, Heading4, Title, Subtitle, Quote, IntenseQuote, ListParagraph, NoSpacing.',
        },
        location: {
          type: 'string',
          enum: ['start', 'end', 'before_paragraph', 'after_paragraph', 'after_selection', 'replace_selection'],
          description:
            "Where to insert. Defaults to 'end' (append) if omitted and target_query is not set. 'start'/'end': document body. 'before_paragraph'/'after_paragraph': requires paragraph_index. 'replace_selection'/'after_selection': cursor-relative. Omit when using target_query.",
        },
        target_query: {
          type: 'string',
          description:
            'Find this text and replace it with the inserted content. One-call alternative to select_text+insert_content(replace_selection). Mutually exclusive with location. MUST be <= 255 characters (Word.search limit). Use a SHORT unique phrase — 5–15 words from the START of the text you want to replace — NOT the full paragraph. Send raw text, no JSON backslash-escaping (just use normal quotes). If multiple matches, set match_index. For multi-paragraph replacements, use location="before_paragraph"/"after_paragraph" with paragraph_index instead.',
        },
        match_index: {
          type: 'number',
          description:
            'Which target_query match to replace (0-based). Omit for the first match.',
        },
        paragraph_index: {
          type: 'number',
          description:
            "Zero-based paragraph index from get_document. Required when location is 'before_paragraph' or 'after_paragraph'.",
        },
        alignment: {
          type: 'string',
          enum: ['left', 'center', 'right', 'justified'],
          description:
            'Applied to every inserted paragraph. Use this instead of a follow-up format_paragraph call.',
        },
        space_before: {
          type: 'number',
          description: 'Space above each inserted paragraph in points (>= 0). Fused paragraph formatting.',
        },
        space_after: {
          type: 'number',
          description: 'Space below each inserted paragraph in points (>= 0). Fused paragraph formatting.',
        },
        line_spacing: {
          type: 'number',
          description: 'Line spacing within each inserted paragraph in points (> 0).',
        },
        left_indent: {
          type: 'number',
          description: 'Left indent for each inserted paragraph in points (72pt = 1 inch).',
        },
        right_indent: {
          type: 'number',
          description: 'Right indent for each inserted paragraph in points.',
        },
        first_line_indent: {
          type: 'number',
          description:
            'First-line indent in points. Positive = standard indent. Negative = hanging indent.',
        },
      },
    },
    permission: true,
    prerequisites: (args) => {
      const loc = args.location as string;
      if (typeof args.target_query === 'string') return null;
      if (loc === 'replace_selection' || loc === 'after_selection') return { needsSelection: true };
      return null;
    },
    validateInput: (args) => {
      const location = args.location as string | undefined;
      const style = typeof args.style === 'string' ? args.style : undefined;
      const text = typeof args.text === 'string' ? args.text : undefined;
      const blocks = Array.isArray(args.blocks)
        ? (args.blocks as Array<{ text: string; style?: string }>)
        : undefined;
      const paragraphIdx = typeof args.paragraph_index === 'number' ? args.paragraph_index : undefined;
      const targetQuery = typeof args.target_query === 'string' ? args.target_query : undefined;

      if (text !== undefined && blocks !== undefined) {
        return {
          error: "Provide either 'text' or 'blocks', not both.",
          recovery: 'Remove one of text or blocks.',
        };
      }
      if (text === undefined && blocks === undefined) {
        return {
          error: 'Provide either text or blocks.',
          recovery: 'Use text for a single paragraph, or blocks for multiple paragraphs.',
        };
      }
      if (targetQuery !== undefined && location !== undefined) {
        return {
          error: 'Provide target_query OR location, not both.',
          recovery: 'target_query replaces found text; location inserts at a position. Pick one.',
        };
      }
      // If neither is provided, execute() defaults location to "end" (safe append).
      if (targetQuery !== undefined) {
        if (targetQuery.trim().length === 0) {
          return { error: 'target_query is empty.', recovery: 'Provide the text to find and replace.' };
        }
        // Word.body.search has a ~255-char limit. Sentences often exceed it.
        if (targetQuery.length > 255) {
          return {
            error: `target_query is ${targetQuery.length} characters; Word.search caps at 255.`,
            recovery:
              'Use a short unique phrase (5–15 words) from the START of the text you want to replace. If the target spans multiple paragraphs, switch to location="before_paragraph" / "after_paragraph" with paragraph_index from get_document instead.',
          };
        }
        // Detect backslash-quote sequences that usually indicate double-escaping.
        if (/\\["'`]/.test(targetQuery)) {
          return {
            error: 'target_query appears to contain escape sequences (backslash + quote). Word.search treats these literally.',
            recovery:
              'Send the raw text as you would type it — JSON will handle quoting automatically. E.g., pass target_query: "He said \\"hi\\"" NOT target_query: "He said \\\\\\"hi\\\\\\"".',
          };
        }
      }
      if ((location === 'before_paragraph' || location === 'after_paragraph') && paragraphIdx === undefined) {
        return {
          error: `paragraph_index is required when location is '${location}'.`,
          recovery: 'Call get_document to find paragraph indices, then provide paragraph_index.',
        };
      }
      if (text !== undefined) {
        if (!text || text.trim().length === 0) {
          return { error: 'text parameter is empty.', recovery: 'Provide the text content you want to insert.' };
        }
        if (style && !VALID_STYLES.includes(style as (typeof VALID_STYLES)[number])) {
          return {
            error: `Unknown style "${style}".`,
            recovery: `Use one of: ${VALID_STYLES.join(', ')}.`,
          };
        }
      }
      if (blocks !== undefined) {
        if (blocks.length === 0) {
          return {
            error: 'blocks array is empty.',
            recovery: 'Provide at least one block with a text property.',
          };
        }
        for (let i = 0; i < blocks.length; i++) {
          const block = blocks[i]!;
          if (!block.text || block.text.trim().length === 0) {
            return {
              error: `blocks[${i}].text is empty.`,
              recovery: 'Every block must have non-empty text.',
            };
          }
          if (block.style && !VALID_STYLES.includes(block.style as (typeof VALID_STYLES)[number])) {
            return {
              error: `blocks[${i}] has unknown style "${block.style}".`,
              recovery: `Use one of: ${VALID_STYLES.join(', ')}.`,
            };
          }
        }
      }
      if (typeof args.space_before === 'number' && (args.space_before as number) < 0) {
        return { error: 'space_before must be >= 0.', recovery: 'Use 0 or a positive value in points.' };
      }
      if (typeof args.space_after === 'number' && (args.space_after as number) < 0) {
        return { error: 'space_after must be >= 0.', recovery: 'Use 0 or a positive value in points.' };
      }
      if (typeof args.line_spacing === 'number' && (args.line_spacing as number) <= 0) {
        return { error: 'line_spacing must be > 0.', recovery: 'Use a positive value in points.' };
      }
      return null;
    },
    getActivityDescription: (args) => {
      if (typeof args.target_query === 'string') {
        const q = args.target_query.length > 40 ? args.target_query.slice(0, 40) + '…' : args.target_query;
        return `Replacing "${q}"`;
      }
      const location = typeof args.location === 'string' ? args.location : 'end';
      if (Array.isArray(args.blocks)) {
        return `Inserting ${args.blocks.length} block${args.blocks.length === 1 ? '' : 's'} at ${location}`;
      }
      if (typeof args.text === 'string') {
        return `Inserting ${args.text.length} chars at ${location}`;
      }
      return `Inserting content at ${location}`;
    },
    execute: async (args) => {
      // Default to "end" (safe append) when the model forgets to specify a location
      // and isn't using target_query. Matches the description; avoids a wasted round-trip.
      const targetQueryPresent = typeof args.target_query === 'string';
      const location =
        typeof args.location === 'string'
          ? (args.location as string)
          : targetQueryPresent
            ? undefined
            : 'end';
      const style = typeof args.style === 'string' ? args.style : undefined;
      const text = typeof args.text === 'string' ? args.text : undefined;
      const blocks = Array.isArray(args.blocks)
        ? (args.blocks as Array<{ text: string; style?: string }>)
        : undefined;
      const paragraphIdx = typeof args.paragraph_index === 'number' ? args.paragraph_index : undefined;
      const targetQuery = typeof args.target_query === 'string' ? args.target_query : undefined;
      const matchIdx = typeof args.match_index === 'number' ? args.match_index : 0;

      const paraProps = {
        alignment: typeof args.alignment === 'string' ? (args.alignment as string) : undefined,
        space_before: typeof args.space_before === 'number' ? (args.space_before as number) : undefined,
        space_after: typeof args.space_after === 'number' ? (args.space_after as number) : undefined,
        line_spacing: typeof args.line_spacing === 'number' ? (args.line_spacing as number) : undefined,
        left_indent: typeof args.left_indent === 'number' ? (args.left_indent as number) : undefined,
        right_indent: typeof args.right_indent === 'number' ? (args.right_indent as number) : undefined,
        first_line_indent:
          typeof args.first_line_indent === 'number' ? (args.first_line_indent as number) : undefined,
      };

      return await Word.run(async (context) => {
        const items: Array<{ text: string; style?: string }> = blocks ?? [{ text: text!, style }];
        let totalChars = 0;
        const previews: Array<{ text: string; style: string }> = [];
        const paragraphsToFormat: Word.Paragraph[] = [];

        // Resolve a single anchor paragraph up front based on the location.
        // All blocks are inserted in order relative to this anchor, advancing
        // the cursor after each one. This prevents "start" / "after_paragraph"
        // / "after_selection" from reversing block order when there are 2+ items.
        type FirstInsert = (text: string) => Word.Paragraph;
        let firstInsert: FirstInsert;

        if (targetQuery !== undefined) {
          const results = context.document.body.search(targetQuery, { matchCase: false });
          results.load('items');
          await context.sync();
          if (results.items.length === 0) {
            return JSON.stringify({
              error: `target_query "${preview(targetQuery, 60)}" not found.`,
              recovery: 'Use get_document to verify the exact text.',
            });
          }
          if (matchIdx < 0 || matchIdx >= results.items.length) {
            return JSON.stringify({
              error: `match_index ${matchIdx} out of range (${results.items.length} matches).`,
              recovery: `Use 0 to ${results.items.length - 1}.`,
            });
          }
          const hit = results.items[matchIdx]!;
          firstInsert = (text) => {
            // Replace the paragraph containing the match with a fresh one carrying the first block.
            const parentPara = hit.paragraphs.getFirst();
            const para = parentPara.insertParagraph(text, Word.InsertLocation.before);
            parentPara.delete();
            return para;
          };
        } else if (location === 'before_paragraph' || location === 'after_paragraph') {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();
          const p = paragraphs.items[paragraphIdx!];
          if (!p) {
            return JSON.stringify({
              error: `paragraph_index ${paragraphIdx} out of range (document has ${paragraphs.items.length} paragraphs).`,
              recovery: `Use 0 to ${paragraphs.items.length - 1}. Call get_document for valid indices.`,
            });
          }
          const loc =
            location === 'before_paragraph' ? Word.InsertLocation.before : Word.InsertLocation.after;
          firstInsert = (text) => p.insertParagraph(text, loc);
        } else if (location === 'replace_selection') {
          const selection = context.document.getSelection();
          firstInsert = (text) => {
            selection.insertText('', Word.InsertLocation.replace);
            return selection.insertParagraph(text, Word.InsertLocation.after);
          };
        } else if (location === 'after_selection') {
          const selection = context.document.getSelection();
          firstInsert = (text) => selection.insertParagraph(text, Word.InsertLocation.after);
        } else {
          // start / end (document-body-level insertion)
          const loc = location === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
          firstInsert = (text) => context.document.body.insertParagraph(text, loc);
        }

        let cursor: Word.Paragraph | undefined;
        for (let i = 0; i < items.length; i++) {
          const item = items[i]!;
          const paragraph =
            i === 0
              ? firstInsert(item.text)
              : cursor!.insertParagraph(item.text, Word.InsertLocation.after);

          if (item.style) {
            paragraph.styleBuiltIn = item.style as Word.BuiltInStyleName;
          }
          paragraphsToFormat.push(paragraph);
          cursor = paragraph;

          totalChars += item.text.length;
          previews.push({
            text: preview(item.text),
            style: item.style ?? 'plain text',
          });
        }

        for (const p of paragraphsToFormat) {
          if (paraProps.alignment) {
            const alignMap: Record<string, Word.Alignment> = {
              left: Word.Alignment.left,
              center: Word.Alignment.centered,
              right: Word.Alignment.right,
              justified: Word.Alignment.justified,
            };
            p.alignment = alignMap[paraProps.alignment]!;
          }
          if (paraProps.space_before !== undefined) p.spaceBefore = paraProps.space_before;
          if (paraProps.space_after !== undefined) p.spaceAfter = paraProps.space_after;
          if (paraProps.line_spacing !== undefined) p.lineSpacing = paraProps.line_spacing;
          if (paraProps.left_indent !== undefined) p.leftIndent = paraProps.left_indent;
          if (paraProps.right_indent !== undefined) p.rightIndent = paraProps.right_indent;
          if (paraProps.first_line_indent !== undefined) p.firstLineIndent = paraProps.first_line_indent;
        }

        await context.sync();

        const appliedParaFormat: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(paraProps)) {
          if (v !== undefined) appliedParaFormat[k] = v;
        }

        const common = {
          location: location ?? (targetQuery !== undefined ? 'target_query' : undefined),
          ...(Object.keys(appliedParaFormat).length ? { paragraph_format: appliedParaFormat } : {}),
        };

        if (blocks) {
          return JSON.stringify({
            success: true,
            blocks_inserted: items.length,
            previews,
            total_chars: totalChars,
            ...common,
          });
        }

        return JSON.stringify({
          success: true,
          inserted: preview(text!),
          style: style ?? 'plain text (no style)',
          char_count: text!.length,
          ...common,
        });
      });
    },
  }),

  buildWordTool({
    name: 'insert_table',
    description:
      'Insert a new table at document start or end. Pass data (2D string array — first row is the header) in the same call so cells are populated up front; avoid insert_table + repeated edit_table calls. For modifying existing tables, use edit_table.',
    parameters: {
      type: 'object',
      properties: {
        rows: {
          type: 'number',
          description:
            'Total number of rows including the header row. Must be a positive integer. Should match data.length when data is provided. Example: 4 rows for a 3-data-row table with a header.',
        },
        columns: {
          type: 'number',
          description:
            'Number of columns. Must be a positive integer. Should match the length of each inner array in data.',
        },
        location: {
          type: 'string',
          enum: ['start', 'end'],
          description:
            "Where in the document body to insert: 'start' (before all content) or 'end' (after all content). Paragraph-relative or selection-relative insertion is not supported — insert, then move with edit_table if needed.",
        },
        data: {
          type: 'array',
          description:
            'Required in practice — an empty table is rarely useful. 2D string array that populates the table. First inner array is the header row. Example: [["Name","Age"],["Alice","30"],["Bob","25"]]. Each inner array is one row. If data has fewer rows/columns than specified, extra cells remain empty.',
        },
      },
      required: ['rows', 'columns', 'location'],
    },
    permission: true,
    validateInput: (args) => {
      const rows = args.rows as number;
      const columns = args.columns as number;
      if (!Number.isInteger(rows) || rows < 1) {
        return { error: 'rows must be a positive integer.', recovery: 'Provide rows >= 1.' };
      }
      if (!Number.isInteger(columns) || columns < 1) {
        return { error: 'columns must be a positive integer.', recovery: 'Provide columns >= 1.' };
      }
      if (args.data) {
        const data = args.data as unknown[];
        if (!Array.isArray(data) || !data.every((row) => Array.isArray(row))) {
          return {
            error: 'data must be a 2D array of strings.',
            recovery: 'Format: [["cell1","cell2"],["cell3","cell4"]]',
          };
        }
        const data2d = data as string[][];
        if (data2d.length > rows) {
          return {
            error: `data has ${data2d.length} rows but table only has ${rows} rows.`,
            recovery: `Set rows to ${data2d.length}, or trim data to ${rows} rows.`,
          };
        }
        const maxCols = Math.max(...data2d.map((r) => r.length));
        if (maxCols > columns) {
          return {
            error: `data has rows with up to ${maxCols} columns but table only has ${columns} columns.`,
            recovery: `Set columns to ${maxCols}, or trim data rows to ${columns} items.`,
          };
        }
      }
      return null;
    },
    getActivityDescription: (args) =>
      `Inserting ${args.rows}×${args.columns} table at ${args.location}`,
    execute: async (args) => {
      const rows = args.rows as number;
      const columns = args.columns as number;
      return await Word.run(async (context) => {
        const loc = args.location === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
        const table = context.document.body.insertTable(rows, columns, loc);

        let cellsPopulated = 0;
        if (args.data) {
          const data = args.data as string[][];
          for (let r = 0; r < data.length && r < rows; r++) {
            for (let c = 0; c < (data[r]?.length ?? 0) && c < columns; c++) {
              table.getCell(r, c).body.insertText(data[r]![c]!, Word.InsertLocation.replace);
              cellsPopulated++;
            }
          }
        }
        await context.sync();

        return JSON.stringify({
          success: true,
          rows,
          columns,
          cells_populated: cellsPopulated,
          location: args.location,
        });
      });
    },
  }),

  buildWordTool({
    name: 'insert_image',
    description:
      "Insert a base64-encoded image (PNG, JPEG, or GIF) into the document. The base64 string must be raw data without a data URI prefix (no 'data:image/png;base64,' prefix). Set both width and height in points (72pt = 1 inch) to maintain aspect ratio, or omit both for original size. Cannot modify or resize existing images — only insert new ones. Returns the location and dimensions applied.",
    parameters: {
      type: 'object',
      properties: {
        base64: {
          type: 'string',
          description:
            "Raw base64-encoded image data. Do NOT include a data URI prefix like 'data:image/png;base64,'. Must be valid base64 encoding of a PNG, JPEG, or GIF image. Example (truncated): 'iVBORw0KGgoAAAANSUhEUgAA...'.",
        },
        location: {
          type: 'string',
          enum: ['start', 'end', 'after_selection'],
          description:
            "Where to insert: 'start' (before all content), 'end' (after all content), 'after_selection' (after the current cursor position — call get_selection first to verify the selection exists).",
        },
        width: {
          type: 'number',
          description:
            'Image width in points (72 points = 1 inch). Set both width and height to control size. Omit both to use the original image dimensions. Must be > 0.',
        },
        height: {
          type: 'number',
          description:
            'Image height in points (72 points = 1 inch). Set both width and height to control size. Omit both to use the original image dimensions. Must be > 0.',
        },
      },
      required: ['base64', 'location'],
    },
    permission: true,
    prerequisites: (args) =>
      args.location === 'after_selection' ? { needsSelection: true } : null,
    validateInput: (args) => {
      const base64 = args.base64 as string;
      if (!base64 || base64.trim().length === 0) {
        return { error: 'base64 is empty.', recovery: 'Provide valid base64-encoded image data.' };
      }
      if (base64.startsWith('data:')) {
        return {
          error: 'base64 contains a data URI prefix.',
          recovery: 'Remove the "data:image/...;base64," prefix and pass only the raw base64 string.',
        };
      }
      if (base64.length < 20) {
        return {
          error: 'base64 data is too short to be a valid image.',
          recovery: 'Provide the full base64-encoded image data (PNG, JPEG, or GIF).',
        };
      }
      if (typeof args.width === 'number' && args.width <= 0) {
        return { error: 'width must be positive.', recovery: 'Provide width in points (72pt = 1 inch).' };
      }
      if (typeof args.height === 'number' && args.height <= 0) {
        return { error: 'height must be positive.', recovery: 'Provide height in points (72pt = 1 inch).' };
      }
      return null;
    },
    getActivityDescription: (args) => `Inserting image at ${args.location}`,
    execute: async (args) => {
      const base64 = args.base64 as string;
      return await Word.run(async (context) => {
        let image: Word.InlinePicture;
        if (args.location === 'after_selection') {
          const selection = context.document.getSelection();
          image = selection.insertInlinePictureFromBase64(base64, Word.InsertLocation.after);
        } else {
          const loc = args.location === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
          image = context.document.body.insertInlinePictureFromBase64(base64, loc);
        }
        if (typeof args.width === 'number') image.width = args.width;
        if (typeof args.height === 'number') image.height = args.height;
        await context.sync();

        return JSON.stringify({
          success: true,
          location: args.location,
          width: typeof args.width === 'number' ? args.width : 'original',
          height: typeof args.height === 'number' ? args.height : 'original',
        });
      });
    },
  }),

  buildWordTool({
    name: 'undo_redo',
    description:
      "Undo or redo document operations in one call. Pass count to undo/redo N steps. Faster and more reliable than manually reversing. Do not use undo to reverse operations you can fix directly (e.g., use search_document for a typo). Returns a diff of paragraph text/style changes.",
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          enum: ['undo', 'redo'],
          description:
            "'undo' reverses recent changes; 'redo' reapplies previously-undone ones (only available immediately after an undo).",
        },
        count: {
          type: 'number',
          description:
            'How many steps to undo/redo in a single call. Defaults to 1. Must be >= 1. Use this to batch instead of making N separate undo_redo calls.',
        },
      },
      required: ['action'],
    },
    permission: true,
    isDestructive: () => true,
    getActivityDescription: (args) => (args.action === 'redo' ? 'Redoing last change' : 'Undoing last change'),
    execute: async (args) => {
      return await Word.run(async (context) => {
        const parasBefore = context.document.body.paragraphs;
        parasBefore.load('items');
        await context.sync();
        for (const p of parasBefore.items) p.load('text,styleBuiltIn');
        await context.sync();
        const before = parasBefore.items.map((p) => ({ text: p.text, style: p.styleBuiltIn }));

        const count = typeof args.count === 'number' && args.count > 0 ? Math.floor(args.count) : 1;
        if (args.action === 'undo') {
          (context.document as any).undo(count);
        } else {
          (context.document as any).redo(count);
        }
        await context.sync();

        const parasAfter = context.document.body.paragraphs;
        parasAfter.load('items');
        await context.sync();
        for (const p of parasAfter.items) p.load('text,styleBuiltIn');
        await context.sync();
        const after = parasAfter.items.map((p) => ({ text: p.text, style: p.styleBuiltIn }));

        const changes: Array<{
          index: number;
          before?: { text: string; style: string };
          after?: { text: string; style: string };
        }> = [];
        const maxLen = Math.max(before.length, after.length);
        for (let i = 0; i < maxLen; i++) {
          const b = before[i];
          const a = after[i];
          if (!b) {
            changes.push({ index: i, after: a });
          } else if (!a) {
            changes.push({ index: i, before: b });
          } else if (b.text !== a.text || b.style !== a.style) {
            changes.push({ index: i, before: b, after: a });
          }
        }

        return JSON.stringify({
          success: true,
          action: args.action,
          changes:
            changes.length > 0
              ? changes
              : 'no visible text/style changes (may be a formatting-only change)',
          paragraph_count: { before: before.length, after: after.length },
        });
      });
    },
  }),

  buildWordTool({
    name: 'delete_content',
    description:
      "Delete entire PARAGRAPHS. Use paragraph_index for one paragraph or start_index+end_index for a range. For deleting inline text WITHIN paragraphs (preserving paragraph structure), use search_document(query, replace_with=\"\") instead — do NOT use delete_content's query mode (kept for back-compat only). For removing list FORMATTING without deleting text, use manage_list action='remove'. Paragraph deletion shifts subsequent indices — re-call get_document before further index-based edits.",
    parameters: {
      type: 'object',
      properties: {
        paragraph_index: {
          type: 'number',
          description:
            'Delete a single paragraph by zero-based index. Mutually exclusive with start_index/end_index and query. Get valid indices from get_document.',
        },
        start_index: {
          type: 'number',
          description:
            'Start of paragraph range to delete (zero-based, inclusive). Requires end_index. Mutually exclusive with paragraph_index and query.',
        },
        end_index: {
          type: 'number',
          description:
            'End of paragraph range to delete (zero-based, inclusive). Requires start_index. Must be >= start_index.',
        },
        query: {
          type: 'string',
          description:
            'Delete all text matching this query while preserving paragraph structure. Case-insensitive by default. Use 3+ unique words to avoid over-matching. Mutually exclusive with paragraph_index/start_index.',
        },
        match_case: {
          type: 'boolean',
          description: 'Set to true for case-sensitive query matching. Defaults to false.',
        },
      },
    },
    permission: true,
    isDestructive: () => true,
    validateInput: (args) => {
      const hasParagraph = typeof args.paragraph_index === 'number';
      const hasRange = typeof args.start_index === 'number' || typeof args.end_index === 'number';
      const hasQuery = typeof args.query === 'string';

      const modeCount = [hasParagraph, hasRange, hasQuery].filter(Boolean).length;
      if (modeCount === 0) {
        return {
          error: 'No deletion mode specified.',
          recovery:
            'Provide paragraph_index (single), start_index + end_index (range), or query (text match).',
        };
      }
      if (modeCount > 1) {
        return {
          error: 'Multiple deletion modes specified.',
          recovery: 'Use only one: paragraph_index, start_index + end_index, or query.',
        };
      }
      if (hasParagraph && (args.paragraph_index as number) < 0) {
        return {
          error: 'paragraph_index must be >= 0.',
          recovery: 'Use 0 for the first paragraph. Get valid indices from get_document.',
        };
      }
      if (hasRange) {
        if (typeof args.start_index !== 'number' || typeof args.end_index !== 'number') {
          return {
            error: 'Both start_index and end_index are required for range deletion.',
            recovery: 'Provide both start_index and end_index.',
          };
        }
        if ((args.start_index as number) < 0 || (args.end_index as number) < 0) {
          return { error: 'Indices must be >= 0.', recovery: 'Use 0 for the first paragraph.' };
        }
        if ((args.start_index as number) > (args.end_index as number)) {
          return {
            error: `start_index (${args.start_index}) is greater than end_index (${args.end_index}).`,
            recovery: `Swap the values: start_index=${args.end_index}, end_index=${args.start_index}.`,
          };
        }
      }
      if (hasQuery && (!args.query || (args.query as string).trim().length === 0)) {
        return { error: 'query is empty.', recovery: 'Provide the text to search for and delete.' };
      }
      return null;
    },
    getActivityDescription: (args) => {
      if (typeof args.paragraph_index === 'number') return `Deleting paragraph ${args.paragraph_index}`;
      if (typeof args.start_index === 'number')
        return `Deleting paragraphs ${args.start_index}–${args.end_index}`;
      if (typeof args.query === 'string') {
        const q = args.query.length > 40 ? args.query.slice(0, 40) + '…' : args.query;
        return `Deleting "${q}"`;
      }
      return 'Deleting content';
    },
    execute: async (args) => {
      const hasParagraph = typeof args.paragraph_index === 'number';
      const hasRange = typeof args.start_index === 'number';

      if (hasParagraph) {
        const idx = args.paragraph_index as number;
        return await Word.run(async (context) => {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();

          const p = paragraphs.items[idx];
          if (!p) {
            return JSON.stringify({
              error: `paragraph_index ${idx} out of range (document has ${paragraphs.items.length} paragraphs).`,
              recovery: `Use 0 to ${paragraphs.items.length - 1}. Call get_document for valid indices.`,
            });
          }

          p.load('text');
          await context.sync();
          const deletedText = p.text;
          p.delete();
          await context.sync();

          return JSON.stringify({
            success: true,
            mode: 'paragraph',
            deleted_preview: preview(deletedText),
            count: 1,
            warning: 'Paragraph indices have shifted. Call get_document for updated indices.',
          });
        });
      }

      if (hasRange) {
        const startIdx = args.start_index as number;
        const endIdx = args.end_index as number;
        return await Word.run(async (context) => {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();

          if (endIdx >= paragraphs.items.length) {
            return JSON.stringify({
              error: `end_index ${endIdx} out of range (document has ${paragraphs.items.length} paragraphs).`,
              recovery: `Use 0 to ${paragraphs.items.length - 1}. Call get_document for valid indices.`,
            });
          }

          for (let i = endIdx; i >= startIdx; i--) {
            paragraphs.items[i]!.delete();
          }
          await context.sync();

          const count = endIdx - startIdx + 1;
          return JSON.stringify({
            success: true,
            mode: 'range',
            count,
            deleted_range: { start: startIdx, end: endIdx },
            warning: 'Paragraph indices have shifted. Call get_document for updated indices.',
          });
        });
      }

      const queryText = args.query as string;
      return await Word.run(async (context) => {
        const results = context.document.body.search(queryText, { matchCase: args.match_case === true });
        results.load('text');
        await context.sync();

        if (results.items.length === 0) {
          return JSON.stringify({
            success: true,
            mode: 'query',
            count: 0,
            hint: `No matches found for "${preview(queryText, 60)}". Use get_document to check exact text.`,
          });
        }

        for (const range of results.items) {
          range.insertText('', Word.InsertLocation.replace);
        }
        await context.sync();

        return JSON.stringify({
          success: true,
          mode: 'query',
          query: preview(queryText, 60),
          count: results.items.length,
          hint: `Deleted ${results.items.length} occurrence${results.items.length > 1 ? 's' : ''}. Paragraph structure preserved.`,
        });
      });
    },
  }),

  buildWordTool({
    name: 'edit_table',
    description:
      "Modify an existing table in the document. PREREQUISITE: Call get_document to get table indices, row counts, and column counts. Actions: 'edit_cell' sets a cell's text (requires row, column, value), 'add_rows'/'delete_rows' add or remove rows, 'add_columns'/'delete_columns' add or remove columns. Column operations only work on uniform tables (all rows same cell count). For creating new tables, use insert_table. For reading table data, use get_document. Reversible with undo_redo. Returns the action performed and change details.",
    parameters: {
      type: 'object',
      properties: {
        table_index: {
          type: 'number',
          description:
            'Zero-based index of the table. Get valid indices from get_document (tables[].index). Required for every action.',
        },
        action: {
          type: 'string',
          enum: ['edit_cell', 'add_rows', 'delete_rows', 'add_columns', 'delete_columns'],
          description:
            "Operation to perform on the table. 'edit_cell' requires row + column + value. 'delete_rows' requires row. 'delete_columns' requires column. 'add_rows'/'add_columns' require insert_location.",
        },
        row: {
          type: 'number',
          description: 'Zero-based row index. Required for edit_cell and delete_rows (start row).',
        },
        column: {
          type: 'number',
          description: 'Zero-based column index. Required for edit_cell and delete_columns (start column).',
        },
        value: {
          type: 'string',
          description: 'New cell text for edit_cell. Replaces existing cell content entirely.',
        },
        count: {
          type: 'number',
          description: 'Number of rows/columns to add or delete. Defaults to 1. Must be >= 1.',
        },
        values: {
          type: 'array',
          description:
            "Optional 2D string array to populate added rows/columns. Example: [['A','B'],['C','D']] adds two 2-cell rows. Ignored for edit_cell/delete_*.",
        },
        insert_location: {
          type: 'string',
          enum: ['start', 'end'],
          description:
            "Where to add rows/columns: 'start' (above/left) or 'end' (below/right). Required for add_rows and add_columns.",
        },
      },
      required: ['table_index', 'action'],
    },
    permission: true,
    isDestructive: (args) =>
      args.action === 'delete_rows' || args.action === 'delete_columns' || args.action === 'edit_cell',
    validateInput: (args) => {
      const tableIdx = args.table_index as number;
      const action = args.action as string;
      if (typeof tableIdx !== 'number' || tableIdx < 0) {
        return {
          error: 'table_index must be a non-negative number.',
          recovery: 'Call get_document to see available tables and their indices.',
        };
      }
      if (action === 'edit_cell') {
        if (typeof args.row !== 'number' || typeof args.column !== 'number') {
          return {
            error: 'edit_cell requires row and column.',
            recovery: 'Provide both row and column (zero-based).',
          };
        }
        if (typeof args.value !== 'string') {
          return { error: 'edit_cell requires value.', recovery: 'Provide the new cell text as value.' };
        }
      }
      if (action === 'delete_rows' && typeof args.row !== 'number') {
        return {
          error: 'delete_rows requires row.',
          recovery: 'Provide the zero-based row index to start deleting from.',
        };
      }
      if (action === 'delete_columns' && typeof args.column !== 'number') {
        return {
          error: 'delete_columns requires column.',
          recovery: 'Provide the zero-based column index.',
        };
      }
      if ((action === 'add_rows' || action === 'add_columns') && typeof args.insert_location !== 'string') {
        return {
          error: `${action} requires insert_location.`,
          recovery: "Provide insert_location: 'start' or 'end'.",
        };
      }
      return null;
    },
    getActivityDescription: (args) =>
      `Editing table ${args.table_index}: ${args.action}`,
    execute: async (args) => {
      const tableIdx = args.table_index as number;
      const action = args.action as string;
      return await Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load('items');
        await context.sync();

        const table = tables.items[tableIdx];
        if (!table) {
          return JSON.stringify({
            error: `table_index ${tableIdx} out of range (document has ${tables.items.length} tables).`,
            recovery: `Use 0 to ${tables.items.length - 1}. Call get_document for table info.`,
          });
        }

        const cnt = typeof args.count === 'number' ? args.count : 1;

        switch (action) {
          case 'edit_cell': {
            const cell = table.getCell(args.row as number, args.column as number);
            cell.value = args.value as string;
            await context.sync();
            return JSON.stringify({
              success: true,
              table_index: tableIdx,
              action,
              detail: `Cell (${args.row}, ${args.column}) set to "${preview(args.value as string, 60)}"`,
            });
          }
          case 'add_rows': {
            const loc = args.insert_location === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
            table.addRows(loc, cnt, args.values as string[][] | undefined);
            await context.sync();
            return JSON.stringify({
              success: true,
              table_index: tableIdx,
              action,
              detail: `Added ${cnt} row${cnt > 1 ? 's' : ''} at ${args.insert_location}`,
            });
          }
          case 'delete_rows': {
            table.deleteRows(args.row as number, cnt);
            await context.sync();
            return JSON.stringify({
              success: true,
              table_index: tableIdx,
              action,
              detail: `Deleted ${cnt} row${cnt > 1 ? 's' : ''} starting at row ${args.row}`,
              warning: 'Row indices have shifted. Call get_document for updated table dimensions.',
            });
          }
          case 'add_columns': {
            table.load('isUniform');
            await context.sync();
            if (!table.isUniform) {
              return JSON.stringify({
                error:
                  'add_columns requires a uniform table (all rows must have the same number of cells).',
                recovery:
                  'Check table structure with get_document. Use edit_cell to modify individual cells instead.',
              });
            }
            const colLoc =
              args.insert_location === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
            table.addColumns(colLoc, cnt, args.values as string[][] | undefined);
            await context.sync();
            return JSON.stringify({
              success: true,
              table_index: tableIdx,
              action,
              detail: `Added ${cnt} column${cnt > 1 ? 's' : ''} at ${args.insert_location}`,
            });
          }
          case 'delete_columns': {
            table.load('isUniform');
            await context.sync();
            if (!table.isUniform) {
              return JSON.stringify({
                error: 'delete_columns requires a uniform table.',
                recovery: 'Check table structure with get_document.',
              });
            }
            table.deleteColumns(args.column as number, cnt);
            await context.sync();
            return JSON.stringify({
              success: true,
              table_index: tableIdx,
              action,
              detail: `Deleted ${cnt} column${cnt > 1 ? 's' : ''} starting at column ${args.column}`,
              warning: 'Column indices have shifted. Call get_document for updated table dimensions.',
            });
          }
          default:
            return JSON.stringify({
              error: `Unknown action "${action}".`,
              recovery:
                'Use one of: edit_cell, add_rows, delete_rows, add_columns, delete_columns.',
            });
        }
      });
    },
  }),
];
